import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Spinner } from 'reactstrap';
import './AdminLayout.scss';

const AdminLayout = () => {
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [user, setUser] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        checkUser();

        // Listen for auth changes
        const { data: authListener } = supabase.auth.onAuthStateChange(
            async (event, session) => {
                if (event === 'SIGNED_OUT') {
                    setIsAdmin(false);
                    setUser(null);
                    navigate('/login');
                } else if (session) {
                    await checkAdminRole(session.user);
                }
            }
        );

        return () => {
            authListener.subscription.unsubscribe();
        };
    }, []);

    const checkUser = async () => {
        try {
            const { data: { user }, error } = await supabase.auth.getUser();

            if (error) throw error;

            if (user) {
                await checkAdminRole(user);
            } else {
                setIsAdmin(false);
                setUser(null);
            }
        } catch (error) {
            console.error('Auth error:', error);
            setIsAdmin(false);
        } finally {
            setLoading(false);
        }
    };

    const checkAdminRole = async (user) => {
        try {
            // Check user metadata for admin role
            const userRole = user.user_metadata?.role || 'user';
            const isUserAdmin = userRole === 'admin';

            setUser(user);
            setIsAdmin(isUserAdmin);

            if (!isUserAdmin) {
                navigate('/unauthorized');
            }
        } catch (error) {
            console.error('Role check error:', error);
            setIsAdmin(false);
        }
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/login');
    };

    // Helper function to check if link is active
    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    if (loading) {
        return (
            <div className="admin-auth-loading">
                <Spinner color="primary" />
                <p>Checking authentication...</p>
            </div>
        );
    }

    if (!isAdmin) {
        return <Navigate to="/login" />;
    }

    return (
        <div className="admin-layout">
            {/* Admin Header/Navbar */}
            <header className="admin-header">
                <div className="admin-header-content">
                    <div className="admin-brand">
                        <Link to="/admin/dashboard" className="brand-link">
                            <i className="fas fa-shield-alt"></i>
                            <h2>Admin Dashboard</h2>
                        </Link>
                    </div>

                    <div className="admin-user-info">
                        <div className="user-avatar">
                            {user?.email?.charAt(0).toUpperCase()}
                        </div>
                        <div className="user-details">
                            <span className="user-email">{user?.email}</span>
                            <span className="user-role">
                                <i className="fas fa-user-shield"></i> Admin
                            </span>
                        </div>
                        <button className="logout-btn" onClick={handleLogout}>
                            <i className="fas fa-sign-out-alt"></i> Logout
                        </button>
                    </div>
                </div>

                <nav className="admin-nav">
                    <ul className="admin-nav-menu">
                        <li>
                            <Link
                                to="/admin/dashboard"
                                className={isActive('/admin/dashboard') ? 'active' : ''}
                            >
                                <i className="fas fa-tachometer-alt"></i>
                                Dashboard
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/admin/services"
                                className={isActive('/admin/services') ? 'active' : ''}
                            >
                                <i className="fas fa-cogs"></i>
                                Services
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/admin/users"
                                className={isActive('/admin/users') ? 'active' : ''}
                            >
                                <i className="fas fa-users"></i>
                                Users
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/admin/settings"
                                className={isActive('/admin/settings') ? 'active' : ''}
                            >
                                <i className="fas fa-cog"></i>
                                Settings
                            </Link>
                        </li>
                    </ul>
                </nav>
            </header>

            {/* Main Content */}
            <main className="admin-main">
                <div className="admin-content">
                    <Outlet />
                </div>
            </main>

            {/* Admin Footer */}
            <footer className="admin-footer">
                <p>© {new Date().getFullYear()} Admin Dashboard • v1.0.0</p>
                <div className="admin-stats">
                    <span>Admin User: {user?.email}</span>
                    <span>Last login: {new Date().toLocaleDateString()}</span>
                </div>
            </footer>
        </div>
    );
};

export default AdminLayout;